import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Alert, Box, TextField, Typography } from "@mui/material";
import GreenSwitch from "./GreenSwitch";
import { Link as RouterLink } from "react-router-dom";
import { NeoButton } from "../shared/components";
import IntegrationApi from "../apis/integrationApi";
import { triggerBlobDownload } from "../utils/downloadUtils";
import { teamsMessagingEndpoint } from "./teamsMessagingEndpoint";

const integrationApi = new IntegrationApi();

const SANS = "var(--theo-font-sans)";

const fieldSx = {
  "& .MuiInputBase-root, & .MuiInputLabel-root, & .MuiFormHelperText-root": { fontFamily: SANS, fontSize: 13 },
};

const bodyTypeSx = {
  fontFamily: SANS,
  fontSize: 13,
  color: "#666F8D",
  "& .MuiTypography-root, & .MuiAlert-message, & .MuiButton-root, & ol, & ul, & summary": {
    fontFamily: SANS,
    fontSize: 13,
  },
};

const ENTRA_GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const TEAMS_ADMIN_MANAGE_APPS_URL = "https://admin.teams.microsoft.com/policies/manage-apps";
const TEAMS_ADMIN_PERMISSION_POLICIES_URL = "https://admin.teams.microsoft.com/policies/app-permission";
const AZURE_PORTAL_URL = "https://portal.azure.com";

interface BotCredentialStatus {
  configured?: boolean;
  app_id?: string | null;
  app_tenant_id?: string | null;
  secret_set?: boolean;
  enabled?: boolean;
  notify_assignee_change?: boolean;
  updated_at?: string | null;
}

interface TeamsPermissionCheck {
  name: string;
  granted?: boolean;
}

interface TeamsPermissions {
  catalog?: "granted" | "missing" | "pending" | "unknown";
  checks?: TeamsPermissionCheck[];
  message?: string;
  entra_url?: string;
}

interface ConnectionTestResult {
  ok?: boolean;
  source?: string;
  app_id?: string;
  app_tenant_id?: string;
  permissions?: TeamsPermissions;
  permission?: string;
  already_granted?: boolean;
}

interface InstallLink {
  install_url?: string;
  chat_url?: string;
  app_id?: string;
  catalog_app_id?: string | null;
  link_id_source?: "manifest" | "catalog";
  catalog_lookup?: "found" | "not_in_catalog" | "forbidden" | "unavailable";
}

const StepDetailsContext = React.createContext<((node: React.ReactNode | null) => void) | null>(null);

const Step: React.FC<{ index: number; title: string; children: React.ReactNode }> = ({ index, title, children }) => {
  const [open, setOpen] = useState(false);
  const [hasDetails, setHasDetails] = useState(false);
  const detailsRef = useRef<React.ReactNode>(null);
  const register = useCallback((node: React.ReactNode | null) => {
    detailsRef.current = node;
    setHasDetails((current) => {
      const next = node != null;
      return current === next ? current : next;
    });
  }, []);
  return (
    <StepDetailsContext.Provider value={register}>
      <Box sx={{ mb: 2, p: 1.5, border: "1px solid #E5E7EB", borderRadius: "6px", background: "#fff" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1 }}>
          <Typography sx={{ fontFamily: SANS, fontWeight: 600, fontSize: 14, color: "#161616" }}>
            {`Step ${index}: ${title}`}
          </Typography>
          {hasDetails ? (
            <NeoButton
              variant="secondary"
              size="small"
              onClick={() => setOpen((value) => !value)}
              sx={{ fontFamily: SANS, fontWeight: 600, fontSize: 13, flexShrink: 0 }}
            >
              {open ? "Hide detailed steps" : "Show detailed steps"}
            </NeoButton>
          ) : null}
        </Box>
        <Box sx={bodyTypeSx}>
          {children}
          {open && hasDetails ? <Box sx={{ mt: 1.5 }}>{detailsRef.current}</Box> : null}
        </Box>
      </Box>
    </StepDetailsContext.Provider>
  );
};

const DetailedSteps: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const register = useContext(StepDetailsContext);
  const childrenRef = useRef(children);
  childrenRef.current = children;
  useLayoutEffect(() => {
    register?.(childrenRef.current);
    return () => register?.(null);
  }, [register]);
  return null;
};

const guideLinkSx = {
  color: "#3C5BFF",
  fontFamily: "inherit",
  fontSize: "inherit",
  fontWeight: 600,
  textDecoration: "underline",
};

const GuideLink: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
  <Box component="a" href={href} target="_blank" rel="noopener noreferrer" sx={guideLinkSx}>
    {children}
  </Box>
);

const AppLink: React.FC<{ to: string; children: React.ReactNode }> = ({ to, children }) => (
  <Box component={RouterLink} to={to} sx={guideLinkSx}>
    {children}
  </Box>
);

const Code: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="code"
    sx={{
      display: "block",
      fontFamily: "monospace",
      fontSize: 12,
      background: "#F1F5F9",
      borderRadius: "6px",
      p: 1,
      mt: 1,
      mb: 1,
      whiteSpace: "pre-wrap",
      wordBreak: "break-all",
    }}
  >
    {children}
  </Box>
);

function entraApiPermissionsUrl(appId: string): string {
  const id = appId.trim();
  if (!ENTRA_GUID_RE.test(id)) return "https://entra.microsoft.com/";
  return `https://entra.microsoft.com/#view/Microsoft_AAD_RegisteredApps/ApplicationMenuBlade/~/CallAnAPI/appId/${id}/isMSAApp~/false`;
}

const PERMISSIONS_WINDOW = "theo-teams-api-permissions";

function reservePermissionsWindow(): Window | null {
  const opened = window.open("", PERMISSIONS_WINDOW);
  if (!opened) return null;
  try {
    opened.document.title = "API permissions";
    opened.document.body.innerHTML =
      "<p style=\"font-family:sans-serif;padding:24px\">Finish the Microsoft sign-in. This tab will open the bot’s API permissions when that is done.</p>";
  } catch {
    // A reused tab may already be on Entra. It can still be navigated.
  }
  return opened;
}

function showPermissionsPage(reserved: Window | null, url: string): boolean {
  const target = url.trim();
  if (!target) return false;
  if (reserved && !reserved.closed) {
    reserved.location.href = target;
    reserved.focus();
    return true;
  }
  return Boolean(window.open(target, PERMISSIONS_WINDOW));
}

const ChannelPermissionActions: React.FC<{
  configured: boolean;
  signingIn: boolean;
  granting: boolean;
  testing: boolean;
  grantLabel: string;
  deviceConsent: {
    user_code: string;
    verification_uri: string;
    verification_uri_complete?: string;
  } | null;
  copied: string;
  onSignIn: () => void;
  onGrant: () => void;
  onCopy: (code: string) => void;
}> = ({ configured, signingIn, granting, testing, grantLabel, deviceConsent, copied, onSignIn, onGrant, onCopy }) => (
  <>
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
      <NeoButton variant="secondary" onClick={onSignIn} disabled={signingIn || granting || testing || !configured} sx={{ fontWeight: 600 }}>
        {signingIn ? "Waiting for Microsoft…" : "Sign in as Microsoft 365 admin"}
      </NeoButton>
      <NeoButton variant="secondary" onClick={onGrant} disabled={granting || testing || signingIn || !configured} sx={{ fontWeight: 600 }}>
        {granting ? "Granting…" : grantLabel}
      </NeoButton>
    </Box>
    {deviceConsent ? (
      <Box sx={{ mt: 1.5 }}>
        <Typography sx={{ mb: 1 }}>
          Open{" "}
          <GuideLink href={deviceConsent.verification_uri_complete || deviceConsent.verification_uri}>
            Microsoft sign-in
          </GuideLink>
          , enter this code, and sign in as a Global Administrator, Privileged Role Administrator, or Cloud
          Application Administrator. Accept the permission screen.
        </Typography>
        <Code>{deviceConsent.user_code}</Code>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <NeoButton
            variant="secondary"
            component="a"
            href={deviceConsent.verification_uri_complete || deviceConsent.verification_uri}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontWeight: 600 }}
          >
            Open Microsoft sign-in
          </NeoButton>
          <NeoButton variant="secondary" onClick={() => onCopy(deviceConsent.user_code)} sx={{ fontWeight: 600 }}>
            {copied === "device-code" ? "Copied" : "Copy code"}
          </NeoButton>
        </Box>
      </Box>
    ) : null}
    {signingIn ? (
      <Typography sx={{ mt: 1, color: "#666F8D" }}>Waiting for the Microsoft sign-in.</Typography>
    ) : granting ? (
      <Typography sx={{ mt: 1, color: "#666F8D" }}>
        Adding Team.ReadBasic.All and Channel.ReadBasic.All on the bot app…
      </Typography>
    ) : null}
  </>
);

const TeamsBotOnboarding: React.FC = () => {
  const [status, setStatus] = useState<BotCredentialStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [appId, setAppId] = useState("");
  const [appTenantId, setAppTenantId] = useState("");
  const [secretValue, setSecretValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [granting, setGranting] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [deviceConsent, setDeviceConsent] = useState<{
    user_code: string;
    device_code: string;
    verification_uri: string;
    verification_uri_complete?: string;
    interval: number;
  } | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [installLink, setInstallLink] = useState<InstallLink | null>(null);
  const [catalogAppId, setCatalogAppId] = useState("");
  const [savingCatalog, setSavingCatalog] = useState(false);
  const [savingNotify, setSavingNotify] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [copied, setCopied] = useState("");
  const [lookupNote, setLookupNote] = useState("");
  const [testResult, setTestResult] = useState<ConnectionTestResult | null>(null);
  const [testNote, setTestNote] = useState("");
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const permissionsWindowRef = useRef<Window | null>(null);

  const loadStatus = async () => {
    setLoading(true);
    setError("");
    try {
      const payload = (await integrationApi.getTeamsBotCredentials()) as BotCredentialStatus;
      setStatus(payload || null);
      if (payload?.app_id) setAppId(payload.app_id);
      if (payload?.app_tenant_id) setAppTenantId(payload.app_tenant_id);
      if (payload?.configured) {
        try {
          await refreshInstallLink();
        } catch (linkError: any) {
          setInstallLink(null);
          setError(linkError?.message || "Bot credentials loaded, but the install link could not be built.");
        }
      } else {
        setInstallLink(null);
      }
    } catch (err: any) {
      setError(err?.message || "Unable to load Teams bot status.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once when the panel opens
  }, []);

  useEffect(() => {
    const deviceCode = deviceConsent?.device_code;
    if (!deviceCode) return undefined;
    let cancelled = false;
    let timer = 0;
    const waitSeconds = deviceConsent?.interval || 5;
    const tick = async () => {
      try {
        const payload = (await integrationApi.pollTeamsBotAdminConsent(deviceCode)) as ConnectionTestResult & {
          status?: string;
          interval?: number;
        };
        if (cancelled) return;
        if (payload?.status === "pending") {
          const seconds = Number(payload.interval) || waitSeconds;
          timer = window.setTimeout(tick, seconds * 1000);
          return;
        }
        const result = payload?.ok ? payload : null;
        setTestResult(result);
        setTestNote(result?.permissions?.message || "Team.ReadBasic.All and Channel.ReadBasic.All were granted.");
        setDeviceConsent(null);
        setSigningIn(false);
        permissionsWindowRef.current?.close();
        permissionsWindowRef.current = null;
        if (result?.permissions?.catalog === "granted") {
          try {
            const found = (await integrationApi.lookupTeamsCatalogApp()) as InstallLink;
            applyInstallLink(found || null);
            setLookupNote(lookupResultMessage(found || null));
          } catch {
            // The grant result is already shown.
          }
        }
      } catch (err: any) {
        if (cancelled) return;
        const text = err?.message || "Could not grant AppCatalog.Read.All from the Microsoft sign-in.";
        const entraUrl = typeof err?.response?.data?.entra_url === "string" ? err.response.data.entra_url : "";
        const opened = showPermissionsPage(permissionsWindowRef.current, entraUrl);
        permissionsWindowRef.current = null;
        const shown = entraUrl && !opened
          ? `${text} The permissions page was blocked by the browser. Click Open API permissions in Entra.`
          : text;
        setTestResult({ ok: false, permissions: { catalog: "missing", message: shown, entra_url: entraUrl } });
        setTestNote(shown);
        setError(shown);
        setDeviceConsent(null);
        setSigningIn(false);
      }
    };
    timer = window.setTimeout(tick, waitSeconds * 1000);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [deviceConsent?.device_code, deviceConsent?.interval]);

  const validate = (): string => {
    if (!ENTRA_GUID_RE.test(appId.trim())) return "App ID must be a valid Entra application (client) ID.";
    if (!ENTRA_GUID_RE.test(appTenantId.trim())) return "Tenant ID must be a valid Entra tenant ID.";
    if (!secretValue.trim()) return "Client secret is required. Paste it once; it is stored directly and never shown again.";
    return "";
  };

  const handleNotifyToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!status?.configured) return;
    const next = event.target.checked;
    const previous = status;
    setSavingNotify(true);
    setError("");
    setStatus({ ...status, notify_assignee_change: next });
    try {
      const payload = (await integrationApi.saveTeamsAssigneeNotifications(next)) as BotCredentialStatus;
      setStatus(payload || null);
    } catch (err: any) {
      setStatus(previous);
      setError(err?.message || "Failed to update assignee notifications.");
    } finally {
      setSavingNotify(false);
    }
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    setError("");
    setSaved(false);
    setTestResult(null);
    setTestNote("");
    try {
      const payload = (await integrationApi.saveTeamsBotCredentials({
        app_id: appId.trim(),
        app_tenant_id: appTenantId.trim(),
        secret_value: secretValue.trim(),
      })) as BotCredentialStatus;
      setStatus(payload || null);
      setSecretValue("");
      setSaved(true);
      if (payload?.configured) {
        try {
          await refreshInstallLink();
        } catch (linkError: any) {
          setError(linkError?.message || "Credentials saved, but the install link could not be built.");
        }
      }
    } catch (err: any) {
      setError(err?.message || "Failed to save Teams bot credentials.");
    } finally {
      setSaving(false);
    }
  };

  const connectionTestMessage = (payload: ConnectionTestResult | null): string => {
    if (!payload?.ok) {
      return "Connection test finished without a result from Microsoft. Try again.";
    }
    const where = payload.source === "msp" ? "this organization" : "shared";
    return `Connected (${where} credentials, app ${payload.app_id}). Microsoft accepted the bot sign-in. Continue with the app package.`;
  };

  const testSeverity = (): "success" | "warning" | "error" => {
    if (!testResult?.ok) return "error";
    return "success";
  };

  const handleTest = async () => {
    setTesting(true);
    setError("");
    setTestResult(null);
    setTestNote("");
    try {
      const payload = (await integrationApi.testTeamsBotConnection()) as ConnectionTestResult;
      const result = payload?.ok ? payload : null;
      setTestResult(result);
      const message = connectionTestMessage(result);
      setTestNote(message);
      if (!result) setError(message);
    } catch (err: any) {
      const message = err?.message || "Connection test failed.";
      setTestNote(message);
      setError(message);
    } finally {
      setTesting(false);
    }
  };

  const applyInstallLink = (payload: InstallLink | null) => {
    setInstallLink(payload);
    if (payload?.catalog_app_id) setCatalogAppId(payload.catalog_app_id);
  };

  const refreshInstallLink = async () => {
    const payload = (await integrationApi.getTeamsBotInstallLink()) as InstallLink;
    if (payload?.install_url) {
      applyInstallLink(payload);
      return;
    }
    applyInstallLink(payload || null);
    try {
      const found = (await integrationApi.lookupTeamsCatalogApp()) as InstallLink;
      if (found?.install_url) applyInstallLink(found);
    } catch {
      // Catalog lookup needs AppCatalog.Read.All on the Theo Teams bot app. The admin can paste the App ID.
    }
  };

  const handleCopy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
    } catch {
      setError("Could not copy the link. Select it and copy it manually.");
    }
  };

  const handleSaveCatalog = async () => {
    const next = catalogAppId.trim();
    if (next && !ENTRA_GUID_RE.test(next)) {
      setError("Catalog app ID must be a GUID from Teams admin center.");
      return;
    }
    setSavingCatalog(true);
    setError("");
    try {
      const payload = (await integrationApi.saveTeamsCatalogAppId(next)) as InstallLink;
      applyInstallLink(payload || null);
    } catch (err: any) {
      setError(err?.message || "Failed to save the catalog app ID.");
    } finally {
      setSavingCatalog(false);
    }
  };

  const lookupResultMessage = (payload: InstallLink | null): string => {
    switch (payload?.catalog_lookup) {
      case "found":
        return "Found the catalog App ID. The install link above is ready to send.";
      case "not_in_catalog":
        return "Theo is not in the org catalog yet. Upload the zip in Teams admin center, then try again.";
      case "forbidden":
        if (payload?.install_url) {
          return "Find catalog app ID still cannot read the Teams app catalog. A Delegated AppCatalog.Read.All grant does not count. The install link is already using the App ID in the field. Click Grant catalog permission, then try Find again.";
        }
        return "AppCatalog.Read.All is not granted as an Application permission. A Delegated row with the same name does not count. Click Grant catalog permission, then click Find again.";
      default:
        if (payload?.install_url) {
          return "Could not refresh the catalog App ID from Microsoft Graph. The install link is already using the App ID in the field.";
        }
        return "Could not look up the catalog App ID. Paste it from Manage apps → Theo.";
    }
  };

  const handleAdminSignIn = async () => {
    setError("");
    setTestNote("");
    setDeviceConsent(null);
    setSigningIn(true);
    permissionsWindowRef.current?.close();
    permissionsWindowRef.current = reservePermissionsWindow();
    try {
      const started = (await integrationApi.startTeamsBotAdminConsent()) as {
        user_code?: string;
        device_code?: string;
        verification_uri?: string;
        verification_uri_complete?: string;
        interval?: number;
      };
      if (!started.user_code || !started.device_code) {
        permissionsWindowRef.current?.close();
        permissionsWindowRef.current = null;
        setSigningIn(false);
        setError("Microsoft did not return a sign-in code. Try again.");
        return;
      }
      setDeviceConsent({
        user_code: started.user_code,
        device_code: started.device_code,
        verification_uri: started.verification_uri || "https://microsoft.com/devicelogin",
        verification_uri_complete: started.verification_uri_complete,
        interval: started.interval || 5,
      });
    } catch (err: any) {
      permissionsWindowRef.current?.close();
      permissionsWindowRef.current = null;
      setSigningIn(false);
      const text = err?.message || "Failed to start Microsoft sign-in.";
      const entraUrl = typeof err?.response?.data?.entra_url === "string" ? err.response.data.entra_url : "";
      setTestResult({
        ok: false,
        permissions: { catalog: "missing", message: text, entra_url: entraUrl },
      });
      setTestNote(text);
      setError(text);
    }
  };

  const handleGrant = async () => {
    setGranting(true);
    setError("");
    setTestResult(null);
    setTestNote("");
    try {
      const payload = (await integrationApi.grantTeamsBotPermissions()) as ConnectionTestResult;
      const result = payload?.ok ? payload : null;
      setTestResult(result);
      const message = result?.permissions?.message || "Team.ReadBasic.All and Channel.ReadBasic.All were granted.";
      setTestNote(message);
      if (!result) setError(message);
      if (result?.permissions?.catalog === "granted") {
        try {
          const found = (await integrationApi.lookupTeamsCatalogApp()) as InstallLink;
          applyInstallLink(found || null);
          setLookupNote(lookupResultMessage(found || null));
        } catch {
          // The grant result is already shown. Catalog lookup can be retried.
        }
      }
    } catch (err: any) {
      const message = err?.message || "Could not grant AppCatalog.Read.All.";
      const entraUrl = typeof err?.response?.data?.entra_url === "string" ? err.response.data.entra_url : "";
      setTestResult({
        ok: false,
        permissions: { catalog: "missing", message, entra_url: entraUrl },
      });
      setTestNote(message);
      setError(message);
    } finally {
      setGranting(false);
    }
  };

  const handleLookup = async () => {
    setLookingUp(true);
    setError("");
    setLookupNote("");
    try {
      const payload = (await integrationApi.lookupTeamsCatalogApp()) as InstallLink;
      applyInstallLink(payload || null);
      setLookupNote(lookupResultMessage(payload || null));
    } catch (err: any) {
      const message = err?.message || "Failed to look up the Teams catalog app.";
      setLookupNote(message);
      setError(message);
    } finally {
      setLookingUp(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    setError("");
    try {
      const blob = (await integrationApi.downloadTeamsBotPackage()) as Blob;
      triggerBlobDownload(blob, "theo-teams-app.zip");
    } catch (err: any) {
      setError(err?.message || "Failed to download the app package. Save bot credentials first.");
    } finally {
      setDownloading(false);
    }
  };

  const messagingEndpoint = teamsMessagingEndpoint();

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        background: "#F8FAFC",
        borderRadius: "6px",
        fontFamily: SANS,
        "& .MuiAlert-message": { fontFamily: SANS, fontSize: 13 },
      }}
    >
      <Typography sx={{ fontFamily: SANS, fontWeight: 700, fontSize: 16, color: "#161616", mb: 1 }}>
        Theo Teams app
      </Typography>
      <Typography sx={{ fontFamily: SANS, color: "#666F8D", fontSize: 13, mb: 2 }}>
        Create a bot for this organization, save it here, upload the app package in Teams, then send Theo Users the
        install link.
      </Typography>
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {saved ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          Bot credentials saved. Test the connection, then download the app package.
        </Alert>
      ) : null}
      <Box sx={{ mb: 2, p: 1.5, border: "1px solid #E5E7EB", borderRadius: "6px", background: "#fff" }}>
        <Typography sx={{ fontFamily: SANS, fontWeight: 600, fontSize: 14, color: "#161616" }}>
          Status: {loading ? "Loading…" : status?.configured ? "Configured" : "Not configured"}
        </Typography>
        {!loading && status?.configured ? (
          <Typography sx={{ fontFamily: SANS, color: "#666F8D", fontSize: 13, mt: 0.5 }}>
            {`App ${status.app_id} · tenant ${status.app_tenant_id} · secret ${status.secret_set ? "stored" : "missing"}`}
            {status.updated_at ? ` · updated ${status.updated_at}` : ""}
          </Typography>
        ) : null}
      </Box>

      {!loading ? (
        <Box sx={{ mb: 2, p: 1.5, border: "1px solid #E5E7EB", borderRadius: "6px", background: "#fff" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            <Box>
              <Typography sx={{ fontFamily: SANS, fontWeight: 600, fontSize: 14, color: "#161616" }}>
                Notify on assignment
              </Typography>
              <Typography sx={{ fontFamily: SANS, color: "#666F8D", fontSize: 13, mt: 0.5 }}>
                {status?.configured
                  ? "When a ticket is assigned to a Theo user, Theo messages them in their personal Teams chat."
                  : "Available after the bot is saved. When it is on, assigning a ticket messages that Theo user in Teams."}
              </Typography>
            </Box>
            <GreenSwitch
              checked={Boolean(status?.configured && status?.notify_assignee_change !== false)}
              disabled={!status?.configured || savingNotify}
              onChange={handleNotifyToggle}
              inputProps={{ "aria-label": "Notify on assignment" }}
            />
          </Box>
        </Box>
      ) : null}

      <Step index={1} title="Create the Azure Bot">
        <Typography sx={{ mb: 1.5 }}>
          In this organization’s Microsoft tenant, create a single-tenant Azure Bot, turn on the Teams channel, and set
          the messaging endpoint to this address.
        </Typography>
        <Code>{messagingEndpoint}</Code>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <NeoButton
            variant="secondary"
            onClick={() => handleCopy("endpoint", messagingEndpoint)}
            sx={{ fontWeight: 600 }}
          >
            {copied === "endpoint" ? "Copied" : "Copy messaging endpoint"}
          </NeoButton>
        </Box>
        {messagingEndpoint.startsWith("https://") ? null : (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            Azure only accepts a public https address, so it will reject the localhost address above. Open Theo on{" "}
            <GuideLink href="https://runtheo.app/integrations">runtheo.app</GuideLink>,{" "}
            <GuideLink href="https://dev.runtheo.app/integrations">dev.runtheo.app</GuideLink>,{" "}
            <GuideLink href="https://demo.runtheo.app/integrations">demo.runtheo.app</GuideLink>, or{" "}
            <GuideLink href="https://sandbox.runtheo.app/integrations">sandbox.runtheo.app</GuideLink> and copy the
            endpoint shown there.
          </Alert>
        )}
        <DetailedSteps>
          <Typography sx={{ mb: 1.5 }}>
            You need permission to create an app registration (Application Administrator or Cloud Application
            Administrator) and an Azure subscription in that tenant.
          </Typography>
          <Typography sx={{ fontWeight: 600, color: "#161616", mb: 0.5 }}>Create the bot</Typography>
          <Box component="ol" sx={{ m: 0, mb: 1.5, pl: 2.5 }}>
            <li>
              Sign in to the <GuideLink href={AZURE_PORTAL_URL}>Azure portal</GuideLink> with that tenant.
            </li>
            <li>In the top search box, type Azure Bot and open the Create page.</li>
            <li>Bot handle: a short unique name, such as theo-yourcompany. Letters, numbers, and hyphens.</li>
            <li>Subscription and resource group: this organization’s. Data residency: Global, unless the tenant requires a region.</li>
            <li>Pricing tier: Free (F0) is enough to start.</li>
            <li>
              Type of App: Single Tenant. Creation type: Create new Microsoft App ID. Multi-tenant bots are not
              supported.
            </li>
            <li>Review + create, then Go to resource.</li>
          </Box>
          <Typography sx={{ fontWeight: 600, color: "#161616", mb: 0.5 }}>Turn on the Teams channel</Typography>
          <Box component="ol" sx={{ m: 0, mb: 1.5, pl: 2.5 }}>
            <li>On the bot, under Settings, open Channels.</li>
            <li>Select the Microsoft Teams icon.</li>
            <li>Accept the terms, then on the Messaging tab leave Microsoft Teams Commercial selected, unless this tenant is GCC or another sovereign cloud.</li>
            <li>Choose Apply or Save. The Teams row should show as healthy. Leave the Calling tab off.</li>
          </Box>
          <Typography sx={{ fontWeight: 600, color: "#161616", mb: 0.5 }}>Set the messaging endpoint</Typography>
          <Box component="ol" sx={{ m: 0, mb: 1.5, pl: 2.5 }}>
            <li>On the bot, under Settings, open Configuration.</li>
            <li>
              Paste the messaging endpoint above into Messaging endpoint, then Apply.
            </li>
          </Box>
          <Typography sx={{ fontWeight: 600, color: "#161616", mb: 0.5 }}>Copy the App ID, tenant ID, and secret</Typography>
          <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
            <li>Still on Configuration, copy Microsoft App ID. That is the App (client) ID in the next step.</li>
            <li>Select Manage next to Microsoft App ID. That opens this bot’s app registration on Client secrets.</li>
            <li>Open Overview and copy Directory (tenant) ID. Application (client) ID should match the App ID you just copied.</li>
            <li>Return to Certificates &amp; secrets, then Client secrets, then New client secret.</li>
            <li>Description: Theo Teams. Pick an expiry your policy allows, then Add.</li>
            <li>
              Copy the Value column immediately. The Secret ID is not the secret, and Azure hides the value after you
              leave this page.
            </li>
          </Box>
        </DetailedSteps>
      </Step>

      <Step index={2} title="Save the bot in Theo">
        <Typography sx={{ mb: 1.5 }}>
          Paste the App ID, tenant ID, and client secret from the Azure Bot, then test the sign-in.
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, maxWidth: 560 }}>
          <TextField
            size="small"
            fullWidth
            label="App (client) ID"
            placeholder="8b2b5a00-f94f-479f-984f-94bb16168fb8"
            value={appId}
            onChange={(event) => setAppId(event.target.value)}
            sx={fieldSx}
          />
          <TextField
            size="small"
            fullWidth
            label="Tenant (directory) ID"
            placeholder="97ca193c-cdc4-4c8e-9260-8892b93c64c4"
            value={appTenantId}
            onChange={(event) => setAppTenantId(event.target.value)}
            sx={fieldSx}
          />
          <TextField
            size="small"
            fullWidth
            type="password"
            label="Client secret value"
            placeholder="Paste once — never shown again"
            value={secretValue}
            onChange={(event) => setSecretValue(event.target.value)}
            autoComplete="new-password"
            inputProps={{ "data-lpignore": "true", "data-1p-ignore": true }}
            sx={fieldSx}
          />
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.5 }}>
          <NeoButton variant="neo" onClick={handleSave} disabled={saving} sx={{ fontWeight: 600 }}>
            {saving ? "Saving…" : "Save bot credentials"}
          </NeoButton>
          <NeoButton variant="secondary" onClick={handleTest} disabled={testing || granting || signingIn} sx={{ fontWeight: 600 }}>
            {testing ? "Testing…" : "Test connection"}
          </NeoButton>
        </Box>
        {testing ? (
          <Typography sx={{ mt: 1, color: "#666F8D" }}>
            Checking the bot sign-in with Microsoft…
          </Typography>
        ) : testNote && !signingIn && !granting ? (
          <Alert severity={testSeverity()} sx={{ mt: 1.5 }}>
            {testNote}
          </Alert>
        ) : null}
        <DetailedSteps>
          <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
            <li>Paste the Microsoft App ID into App (client) ID.</li>
            <li>Paste the Directory (tenant) ID into Tenant (directory) ID.</li>
            <li>Paste the client secret Value. Theo stores it once and never shows it again.</li>
            <li>Choose Save bot credentials, then Test connection.</li>
            <li>
              A successful test says Microsoft accepted the bot sign-in. It does not install Theo in Teams. Continue
              to the app package.
            </li>
          </Box>
        </DetailedSteps>
      </Step>

      <Step index={3} title="Grant permission to list Teams channels">
        <Typography sx={{ mb: 1.5 }}>
          Add two Microsoft Graph application permissions on the bot app, then grant admin consent. The agent
          configure page uses them to fill the Teams channel list.
        </Typography>
        <Box sx={{ mb: 1.5 }}>
          <NeoButton
            variant="secondary"
            component="a"
            href={entraApiPermissionsUrl(appId)}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontWeight: 600 }}
          >
            Open API permissions
          </NeoButton>
        </Box>
        <DetailedSteps>
          <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
            <li>Open the bot app in Entra. Use the App (client) ID saved in the previous step.</li>
            <li>Go to API permissions, then Add a permission, then Microsoft Graph, then Application permissions.</li>
            <li>Select Team.ReadBasic.All and Channel.ReadBasic.All, then Add permissions.</li>
            <li>
              Click Grant admin consent. Sign in as a Global Administrator, Privileged Role Administrator, or Cloud
              Application Administrator.
            </li>
            <li>Both rows must say Application in the Type column and Granted in the Status column. A Delegated row does not count.</li>
            <li>Open the agent’s Configure page, Communicate step, and choose the Teams channel.</li>
          </Box>
        </DetailedSteps>
      </Step>

      <Step index={4} title="Upload Theo to Teams">
        <Typography sx={{ mb: 1.5 }}>
          Download the zip, then upload it in Teams admin center → Manage apps. Leave the zip closed.
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
          <NeoButton variant="neo" onClick={handleDownload} disabled={downloading || !status?.configured} sx={{ fontWeight: 600 }}>
            {downloading ? "Preparing…" : "Download app package (.zip)"}
          </NeoButton>
          <NeoButton
            variant="secondary"
            component="a"
            href={TEAMS_ADMIN_MANAGE_APPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontWeight: 600 }}
          >
            Open Teams admin center
          </NeoButton>
        </Box>
        {!status?.configured ? (
          <Typography>
            Save the bot in the previous step first. The package is built from those credentials.
          </Typography>
        ) : null}
        <DetailedSteps>
          <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
            <li>Download the app package. The zip is built from the bot you just saved.</li>
            <li>
              Open Teams admin center → Manage apps.
            </li>
            <li>
              If Upload new app is missing, open Org-wide app settings on that page and turn on custom apps,
              including letting users interact with custom apps. Save, then return to Manage apps.
            </li>
            <li>Choose Upload new app, then Upload an app package, and select theo-teams-app.zip.</li>
            <li>Search for Theo and open it. If the status is Blocked, set it to Allowed.</li>
            <li>
              Open <GuideLink href={TEAMS_ADMIN_PERMISSION_POLICIES_URL}>Permission policies</GuideLink>. The policy
              assigned to Theo Users must allow custom apps, or specifically allow Theo. Otherwise the install link
              opens and Teams still refuses the app.
            </li>
            <li>
              On the Theo page in Manage apps, copy App ID. That catalog App ID is different from the Azure Bot App
              ID in step 1. You need it for the install link.
            </li>
          </Box>
        </DetailedSteps>
      </Step>

      <Step index={5} title="Send Theo Users the install link">
        {!status?.configured ? (
          <Typography>
            Save the bot, upload the package, then paste the catalog App ID from{" "}
            <GuideLink href={TEAMS_ADMIN_MANAGE_APPS_URL}>Teams admin center</GuideLink>.
          </Typography>
        ) : (
          <Box>
            <Typography sx={{ mb: 1.5 }}>
              Paste the App ID from Manage apps → Theo. It is not the Azure Bot App ID.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 1.5 }}>
              <TextField
                size="small"
                label="Teams catalog app ID"
                placeholder="From Teams admin center → Manage apps → Theo"
                value={catalogAppId}
                onChange={(event) => setCatalogAppId(event.target.value)}
                sx={{ ...fieldSx, flex: "1 1 280px" }}
              />
              <NeoButton variant="neo" onClick={handleSaveCatalog} disabled={savingCatalog} sx={{ fontWeight: 600 }}>
                {savingCatalog ? "Saving…" : "Update install link"}
              </NeoButton>
            </Box>
            {installLink?.install_url ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 1 }}>
                <GuideLink href={installLink.install_url}>Install link</GuideLink>
                <Typography component="span">Opens the Add dialog in Teams.</Typography>
                <NeoButton
                  variant="secondary"
                  size="small"
                  onClick={() => handleCopy("install", installLink.install_url || "")}
                  sx={{ fontWeight: 600 }}
                >
                  {copied === "install" ? "Copied" : "Copy"}
                </NeoButton>
              </Box>
            ) : (
              <Typography sx={{ mb: 1.5 }}>
                The install link appears after you paste the catalog App ID and choose Update install link.
              </Typography>
            )}
            {installLink?.chat_url ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 1 }}>
                <GuideLink href={installLink.chat_url}>Chat link</GuideLink>
                <Typography component="span">Opens Theo after the app is added.</Typography>
                <NeoButton
                  variant="secondary"
                  size="small"
                  onClick={() => handleCopy("chat", installLink.chat_url || "")}
                  sx={{ fontWeight: 600 }}
                >
                  {copied === "chat" ? "Copied" : "Copy"}
                </NeoButton>
              </Box>
            ) : null}
            {lookupNote ? (
              <Typography
                sx={{
                  mt: 1,
                  color: installLink?.catalog_lookup === "found"
                    ? "#067647"
                    : installLink?.install_url
                      ? "#666F8D"
                      : "#B54708",
                }}
              >
                {lookupNote}
              </Typography>
            ) : null}
            <DetailedSteps>
              <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
                <li>
                  Paste the catalog App ID from{" "}
                  <GuideLink href={TEAMS_ADMIN_MANAGE_APPS_URL}>Manage apps → Theo → App ID</GuideLink>.
                </li>
                <li>Choose Update install link. The install link appears above.</li>
                <li>Copy that link and send it to Theo Users.</li>
                <li>
                  Do not paste the Azure Bot App ID here. Teams looks that ID up in the public store and shows “This
                  app cannot be found.”
                </li>
              </Box>
            </DetailedSteps>
          </Box>
        )}
        <Box component="details" sx={{ mt: 2, fontFamily: SANS, fontSize: 13 }}>
          <Box component="summary" sx={{ cursor: "pointer", fontFamily: SANS, fontWeight: 600, fontSize: 13, color: "#161616" }}>
            Look up the catalog App ID automatically
          </Box>
          <Typography sx={{ mt: 1, mb: 1 }}>
            Pasting the App ID from{" "}
            <GuideLink href={TEAMS_ADMIN_MANAGE_APPS_URL}>Manage apps</GuideLink> is enough. This asks Microsoft
            Graph for it instead, and needs AppCatalog.Read.All granted as an Application permission on the bot.
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
            <NeoButton variant="secondary" onClick={handleLookup} disabled={lookingUp || !status?.configured} sx={{ fontWeight: 600 }}>
              {lookingUp ? "Looking up…" : "Find catalog app ID"}
            </NeoButton>
          </Box>
          <ChannelPermissionActions
            configured={Boolean(status?.configured)}
            signingIn={signingIn}
            granting={granting}
            testing={testing}
            grantLabel="Grant catalog permission"
            deviceConsent={deviceConsent}
            copied={copied}
            onSignIn={handleAdminSignIn}
            onGrant={handleGrant}
            onCopy={(code) => handleCopy("device-code", code)}
          />
          {!testing && !granting && testResult?.permissions?.entra_url && testResult.permissions.catalog !== "granted" ? (
            <Box sx={{ mt: 1 }}>
              <GuideLink href={testResult.permissions?.entra_url || ""}>Open API permissions in Entra</GuideLink>
            </Box>
          ) : null}
        </Box>
      </Step>

      <Step index={6} title="Have each Theo User open Theo once">
        <Typography>
          Each Theo User adds Theo from the install link and sends one message in the personal chat. Track each
          person on <AppLink to="/theo-users">Theo Users</AppLink>.
        </Typography>
        <DetailedSteps>
          <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
            <li>They already have a Theo User, and that email is the same address they use in Microsoft 365.</li>
            <li>They open the install link while signed into this tenant and choose Add.</li>
            <li>They open the personal chat with Theo and send one message, such as hello.</li>
            <li>After Theo replies, assigning them a ticket sends a Teams message when Notify on assignment is on.</li>
            <li>
              <AppLink to="/theo-users">Theo Users</AppLink> shows Connected after that message. Linked means an Entra
              object ID is saved and they have not messaged Theo yet. Not connected means neither is saved.
            </li>
          </Box>
        </DetailedSteps>
      </Step>
    </Box>
  );
};

export default TeamsBotOnboarding;
